import React, { useEffect, useRef, useState } from 'react';

const BAR_COUNT = 7;

interface ChatInputProps {
    onSendMessage: (text: string, fromVoice?: boolean) => void;
    onClearChat: () => void;
    isLoading: boolean;
    isLive?: boolean;
    isConnecting?: boolean;
    isAwaitingPermission?: boolean;
    onStartLiveSession?: () => void;
    onStopLiveSession?: () => void;
    getUserLevel?: () => number;
    getModelLevel?: () => number;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    onSendMessage,
    onClearChat,
    isLoading,
    isLive = false,
    isConnecting = false,
    isAwaitingPermission = false,
    onStartLiveSession,
    onStopLiveSession,
    getUserLevel,
    getModelLevel,
}) => {
    const [inputText, setInputText] = useState('');
    const userBarsRef = useRef<(HTMLDivElement | null)[]>([]);
    const modelBarsRef = useRef<(HTMLDivElement | null)[]>([]);
    const rafRef = useRef<number>(0);

    // Animate bars from real audio levels
    useEffect(() => {
        if (!isLive) {
            rafRef.current && cancelAnimationFrame(rafRef.current);
            return;
        }

        const animate = () => {
            const userLevel = getUserLevel?.() ?? 0;
            const modelLevel = getModelLevel?.() ?? 0;

            applyLevels(userBarsRef.current, userLevel);
            applyLevels(modelBarsRef.current, modelLevel);

            rafRef.current = requestAnimationFrame(animate);
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, [isLive, getUserLevel, getModelLevel]);

    const handleMicClick = () => {
        if (isLoading || isConnecting) return;
        if (isLive) {
            onStopLiveSession?.();
        } else {
            onStartLiveSession?.();
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputText.trim() && !isLoading && !isLive) {
            onSendMessage(inputText);
            setInputText('');
        }
    };

    return (
        <div className="relative">
        {isAwaitingPermission && <MicPermissionOverlay />}
        <form onSubmit={handleSubmit} className="bg-white border-t border-gray-200 p-3 sm:p-4 flex items-center space-x-2 sm:space-x-3 shadow-top">
            <button
                type="button"
                onClick={onClearChat}
                className="flex-shrink-0 p-3 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                title="Chat leeren & Neustarten"
            >
                <TrashIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <button
                type="button"
                onClick={handleMicClick}
                disabled={isLoading || isConnecting}
                className={`flex-shrink-0 relative p-3 rounded-lg text-white transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0066CC]
                    ${isLoading || isConnecting ? 'bg-gray-400 cursor-not-allowed' : isLive ? 'bg-red-500 hover:bg-red-600' : 'bg-[#0066CC] hover:bg-[#004499]'}`}
                title={isLive ? 'Sprachanruf beenden' : isConnecting ? 'Verbinde...' : 'Sprachanruf starten'}
            >
                {isLive && (
                    <span className="absolute inset-0 rounded-lg bg-red-400 animate-ping opacity-20" />
                )}
                {isConnecting ? (
                    <LoadingDots className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                    <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6" isActive={isLive} />
                )}
            </button>

            {isLive ? (
                <div className="flex-grow min-w-0 flex items-center h-12 px-3 gap-2 border border-green-500 rounded-lg bg-green-50 overflow-hidden">
                    {/* User mic visualizer */}
                    <div className="flex items-end gap-[3px] h-7 flex-shrink-0" title="Dein Mikrofon">
                        <MicSmallIcon />
                        {Array.from({ length: BAR_COUNT }, (_, i) => (
                            <div
                                key={`u${i}`}
                                ref={el => { userBarsRef.current[i] = el; }}
                                className="w-[3px] rounded-sm bg-blue-500"
                                style={{ height: '3px', transition: 'height 60ms ease-out' }}
                            />
                        ))}
                    </div>

                    <div className="w-px h-6 bg-green-300 flex-shrink-0" />

                    {/* AI audio visualizer */}
                    <div className="flex items-end gap-[3px] h-7 flex-shrink-0" title="Watt-Son spricht">
                        <SpeakerSmallIcon />
                        {Array.from({ length: BAR_COUNT }, (_, i) => (
                            <div
                                key={`m${i}`}
                                ref={el => { modelBarsRef.current[i] = el; }}
                                className="w-[3px] rounded-sm bg-green-600"
                                style={{ height: '3px', transition: 'height 60ms ease-out' }}
                            />
                        ))}
                    </div>

                    <span className="text-green-700 text-xs font-medium ml-auto flex-shrink-0">Aktiv</span>
                </div>
            ) : isConnecting ? (
                <div className="flex-grow min-w-0 flex items-center justify-center h-12 px-4 border border-yellow-400 rounded-lg bg-yellow-50">
                    <span className="text-yellow-700 text-sm font-medium">Verbinde...</span>
                </div>
            ) : (
                <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="Schreibe deine Nachricht..."
                    className="flex-grow min-w-0 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0066CC] focus:border-transparent outline-none transition-shadow duration-150 ease-in-out"
                    disabled={isLoading}
                />
            )}

            <button
                type="submit"
                disabled={isLoading || !inputText.trim() || isLive}
                className={`flex-shrink-0 p-3 rounded-lg text-white transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0066CC]
                            ${isLoading || !inputText.trim() || isLive ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#0066CC] hover:bg-[#004499]'}`}
            >
                <SendIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
        </form>
        </div>
    );
};

/**
 * Distribute a single 0–1 level across bars with slight per-bar variation
 * so each bar looks different (like a real audio visualizer).
 */
function applyLevels(bars: (HTMLDivElement | null)[], level: number) {
    const maxH = 26; // px
    const minH = 3;  // px

    for (let i = 0; i < bars.length; i++) {
        const el = bars[i];
        if (!el) continue;

        // Per-bar variation using a simple hash offset
        const phase = Math.sin(Date.now() / 120 + i * 1.8) * 0.3 + 0.7;
        const h = minH + (maxH - minH) * level * phase;
        el.style.height = `${Math.max(minH, Math.min(maxH, h))}px`;
    }
}

// Mic permission overlay
const MicPermissionOverlay: React.FC = () => (
    <div className="absolute bottom-full left-0 right-0 flex justify-center pb-3 animate-[fadeSlideUp_300ms_ease-out_both]">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-5 py-4 flex items-center gap-4 max-w-sm">
            {/* Pulsing mic icon */}
            <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-blue-100 animate-ping opacity-40" />
                <div className="relative w-10 h-10 rounded-full bg-[#0066CC] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3zm6 10a6 6 0 01-12 0M12 19v4" />
                    </svg>
                </div>
            </div>
            {/* Text + arrow */}
            <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">Mikrofonzugriff erlauben</span>
                <span className="text-xs text-gray-500 mt-0.5">Klicke im Browser-Dialog auf &quot;Zulassen&quot;</span>
            </div>
            {/* Bouncing arrow pointing up */}
            <svg className="w-5 h-5 text-[#0066CC] flex-shrink-0 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
        </div>
    </div>
);

// Icons
const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const PhoneIcon: React.FC<{ className?: string; isActive?: boolean }> = ({ className, isActive }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {isActive ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.28 3H5z" />
        ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C7.716 21 2 15.284 2 7V5z" />
        )}
    </svg>
);

const LoadingDots: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="6" cy="12" r="2">
            <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
        </circle>
        <circle cx="12" cy="12" r="2">
            <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.33s" />
        </circle>
        <circle cx="18" cy="12" r="2">
            <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.66s" />
        </circle>
    </svg>
);

const MicSmallIcon: React.FC = () => (
    <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3zm6 10a6 6 0 01-12 0M12 19v4" />
    </svg>
);

const SpeakerSmallIcon: React.FC = () => (
    <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M11 5L6 9H2v6h4l5 4V5z" />
    </svg>
);
